import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Snackbar,
  TextField,
  Typography,
} from "@mui/material";
import { DataGrid, GridColumns } from "@mui/x-data-grid";
import { List } from "@refinedev/mui";
import { config } from "config";
import { SimTask, useSimSocket } from "hooks/useSimSocket";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { handleCommonGetRequest, HttpClient } from "rest-data-provider/utils/tools";

const isActiveTicker = async (ticker: string): Promise<boolean> => {
  try {
    await HttpClient.get(`${config().server.stock}/symbol/rtdata`, {
      params: { symbol: `US-${ticker}` },
    });
    return true;
  } catch (e: any) {
    if (e?.response?.status === 403) return false;
    throw e;
  }
};

const getUser = () => {
  try {
    return JSON.parse(window.sessionStorage.getItem("user") as string);
  } catch {
    return null;
  }
};

const isAuthorized = (): boolean => {
  const user = getUser();
  if (!user) return false;
  if (user.group === 0) return true;
  return user?.authority?.["delist-positions"] === true;
};

const parseJson = (s: string | null) => {
  if (!s) return null;
  try { return JSON.parse(s); } catch { return s; }
};

export const DelistPositionsList: React.FC = () => {
  const authorized = isAuthorized();
  const { connected, fetchTasks, submitDelistRequest } = useSimSocket();

  const [tasks, setTasks] = useState<SimTask[]>([]);
  const [ticker, setTicker] = useState("");
  const [loading, setLoading] = useState(false);
  const [userEmails, setUserEmails] = useState<Record<string, string>>({});
  const fetchedUserIds = useRef<Set<string>>(new Set());

  const [showDialogA, setShowDialogA] = useState(false);
  const [showDialogB, setShowDialogB] = useState(false);
  const [activeTicker, setActiveTicker] = useState<string | null>(null);

  const [resultTask, setResultTask] = useState<SimTask | null>(null);

  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: "success" | "error";
  }>({
    open: false,
    message: "",
    severity: "success",
  });

  const loadTasks = useCallback(async (filterTicker?: string) => {
    try {
      const data = await fetchTasks(20, 0, filterTicker);
      setTasks(data);
    } catch (e: any) {
      setSnackbar({ open: true, message: e.message, severity: "error" });
    }
  }, [fetchTasks]);

  useEffect(() => {
    if (connected) loadTasks();
  }, [connected, loadTasks]);

  useEffect(() => {
    const newIds = tasks.map((t) => t.user_id).filter((id) => id && !fetchedUserIds.current.has(id));
    if (newIds.length === 0) return;
    newIds.forEach((id) => fetchedUserIds.current.add(id));
    Promise.all(newIds.map((id) => handleCommonGetRequest("users", id).then((data) => ({ id, email: data?.email ?? id })))).then((results) => {
      setUserEmails((prev) => {
        const next = { ...prev };
        results.forEach(({ id, email }) => { next[id] = email; });
        return next;
      });
    });
  }, [tasks]);

  const handleDelistClick = async () => {
    if (!ticker.trim()) return;
    console.log(`[delist] Initiating delist for ticker: ${ticker.trim()}`);
    if (!connected) {
      console.warn("[delist] Pre-check failed: socket is not connected");
      setSnackbar({ open: true, message: "Socket is not connected. Please wait and try again.", severity: "error" });
      return;
    }
    console.log("[delist] Pre-check passed: socket is connected");
    setLoading(true);
    try {
      console.log(`[delist] Pre-check: verifying ticker ${ticker.trim()} is not an active symbol`);
      let active: boolean;
      try {
        active = await isActiveTicker(ticker.trim());
      } catch (e: any) {
        console.error(`[delist] Pre-check error: failed to verify ticker ${ticker.trim()}`, e);
        setSnackbar({ open: true, message: `Failed to verify ticker "${ticker.trim()}". Please try again.`, severity: "error" });
        return;
      }
      if (active) {
        console.warn(`[delist] Pre-check failed: ticker ${ticker.trim()} is still active`);
        setActiveTicker(ticker.trim());
        return;
      }
      console.log(`[delist] Pre-check passed: ticker ${ticker.trim()} is not active`);
      const allTasks = await fetchTasks(100, 0);
      const exists = allTasks.some((t) => {
        try { return JSON.parse(t.payload)?.symbol?.toUpperCase() === ticker.trim().toUpperCase(); } catch { return false; }
      });
      if (exists) {
        setShowDialogA(true);
      } else {
        setShowDialogB(true);
      }
    } catch (e: any) {
      setSnackbar({ open: true, message: e.message, severity: "error" });
    } finally {
      setLoading(false);
    }
  };

  const handleDialogAConfirm = () => {
    setShowDialogA(false);
    setShowDialogB(true);
  };

  const handleDialogBConfirm = async () => {
    setShowDialogB(false);
    setLoading(true);
    try {
      await submitDelistRequest(ticker.trim());
      setSnackbar({ open: true, message: "Delist request submitted successfully.", severity: "success" });
      setTicker("");
      await loadTasks();
    } catch (e: any) {
      setSnackbar({ open: true, message: e.message, severity: "error" });
    } finally {
      setLoading(false);
    }
  };

  const columns = React.useMemo<GridColumns<SimTask>>(
    () => [
      { field: "id", headerName: "ID", minWidth: 300, flex: 1 },
      {
        field: "payload",
        headerName: "Ticker",
        minWidth: 100,
        renderCell: ({ value }) => {
          try { return JSON.parse(value)?.symbol ?? "-"; } catch { return "-"; }
        },
      },
      { field: "status", headerName: "Status", minWidth: 120 },
      {
        field: "created_at",
        headerName: "Created At",
        minWidth: 180,
        renderCell: ({ value }) =>
          value ? new Date(value).toLocaleString() : "-",
      },
      {
        field: "execution_at",
        headerName: "Scheduled At",
        minWidth: 180,
        renderCell: ({ value }) =>
          value && value !== 0 ? new Date(value).toLocaleString() : "-",
      },
      {
        field: "finished_at",
        headerName: "Finished At",
        minWidth: 180,
        renderCell: ({ value }) =>
          value && value !== 0 ? new Date(value).toLocaleString() : "-",
      },
      {
        field: "user_id",
        headerName: "Requested By",
        minWidth: 240,
        renderCell: ({ value }) =>
          value ? `${value}: ${userEmails[value] ?? "…"}` : "-",
      },
      {
        field: "actions",
        headerName: "",
        minWidth: 140,
        sortable: false,
        renderCell: ({ row }) => (
          <Button size="small" variant="outlined" onClick={() => setResultTask(row)}>
            Result Detail
          </Button>
        ),
      },
    ],
    [userEmails]
  );

  if (!authorized) {
    return (
      <List>
        <Alert severity="error">You are not authorized to access this page.</Alert>
      </List>
    );
  }

  const resultParsed = parseJson(resultTask?.result ?? null);

  return (
    <List>
      {/* Connection status */}
      <Box mb={2}>
        <Chip
          label={connected ? "Connected" : "Disconnected"}
          color={connected ? "success" : "error"}
          size="small"
        />
      </Box>

      {/* Delist form */}
      <Box display="flex" gap={2} alignItems="center" mb={3}>
        <TextField
          label="Ticker"
          value={ticker}
          onChange={(e) => setTicker(e.target.value.toUpperCase())}
          size="small"
          disabled={loading}
        />
        <Button
          variant="outlined"
          onClick={() => loadTasks(ticker.trim() || undefined)}
          disabled={!connected || loading}
        >
          Search
        </Button>
        <Button
          variant="contained"
          color="error"
          onClick={handleDelistClick}
          disabled={!connected || !ticker.trim() || loading}
        >
          Delist
        </Button>
      </Box>

      {/* Task list */}
      <DataGrid rows={tasks} columns={columns} autoHeight hideFooter />

      {/* Active ticker warning — cannot delist */}
      <Dialog open={!!activeTicker} onClose={() => setActiveTicker(null)}>
        <DialogTitle>Cannot Delist Active Ticker</DialogTitle>
        <DialogContent>
          <DialogContentText>
            <strong>{activeTicker}</strong> is still an active symbol and cannot be delisted.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setActiveTicker(null)} variant="contained">OK</Button>
        </DialogActions>
      </Dialog>

      {/* Dialog A: ticker already has a finished task */}
      <Dialog open={showDialogA} onClose={() => setShowDialogA(false)}>
        <DialogTitle>Ticker Already Processed</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This ticker already has a finished task. Continue anyway?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowDialogA(false)}>Cancel</Button>
          <Button onClick={handleDialogAConfirm} color="warning">Continue</Button>
        </DialogActions>
      </Dialog>

      {/* Dialog B: dangerous action warning */}
      <Dialog open={showDialogB} onClose={() => setShowDialogB(false)}>
        <DialogTitle>Confirm Delist</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Delisting a ticker will close all opening positions of users. Continue anyway?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowDialogB(false)}>Cancel</Button>
          <Button onClick={handleDialogBConfirm} color="error" variant="contained">Continue</Button>
        </DialogActions>
      </Dialog>

      {/* Result Detail modal */}
      <Dialog open={!!resultTask} onClose={() => setResultTask(null)} maxWidth="md" fullWidth>
        <DialogTitle>Result Detail — {parseJson(resultTask?.payload ?? null)?.symbol ?? resultTask?.id}</DialogTitle>
        <DialogContent dividers>
          <Box
            component="pre"
            sx={{ bgcolor: "grey.900", color: "grey.100", p: 2, borderRadius: 1, overflowX: "auto", fontSize: 13 }}
          >
            {resultParsed ? JSON.stringify(resultParsed, null, 2) : "—"}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setResultTask(null)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar((s) => ({ ...s, open: false }))}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </List>
  );
};
